/**
 * LA DESCRIPCION DE MARCA DEL NEGOCIO (FND-T1, mision FONDO-NEGOCIO-DESCRIPCION-V1-001, D-86 a).
 *
 * DE DONDE SALE. Los prompts de los fondos (FND-T2 de Argy-A) necesitan saber que vende o hace el negocio, sus productos
 * tipicos, sus valores y su color. Hasta hoy eso no estaba en ningun sitio: el prompt salia con huecos. El contrato lo
 * confirmo A (#23156): metadata.marca = {descripcion, productosTipicos, valores, colorNombre} y el hex es el
 * metadata.colores_marca.primario que ya existia (no se duplica). Ruta: GET/PUT /api/businesses/{id}/marca.
 *
 * LA REGLA (la misma de la API, para no gastar el viaje):
 *   - completa = descripcion no vacia, 3 a 6 productos tipicos, 2 o 3 valores y un color #RRGGBB
 *   - falta usa los MISMOS nombres de campo que el 422 de los prompts: descripcion, productosTipicos, valores, colorHex
 *   - la lectura tolera camelCase y snake_case; la escritura sale SOLO en camelCase (un snake_case no liga con el DTO)
 *   - una lista se escribe separada por comas o por lineas; se limpia y no repite
 *
 * Sin DOM: lo prueba scripts/check-marca-negocio.mjs.
 */

export const LIMITES = Object.freeze({ productosMin: 3, productosMax: 6, valoresMin: 2, valoresMax: 3 });

/** Los nombres de campo de falta[] (los del 422 de FND-T2) y como se dicen en pantalla. */
export const ETIQUETAS = Object.freeze({
  descripcion: 'Que vende o hace',
  productosTipicos: 'Productos tipicos (3 a 6)',
  valores: 'Valores (2 o 3)',
  colorHex: 'Color corporativo'
});

/** Ejemplos de la tarea (Josse, D-86): solo la descripcion, que es lo que el dueno suele no saber como escribir. */
export const EJEMPLOS = Object.freeze([
  'Alebrijes: restaurante de comida mexicana',
  'Aplastados: hamburguesas tipo sandwich aplastadas',
  'Crocs: zapatos tipo Crocs',
  'Club Campestre: escuela deportiva y alquiler de canchas',
  'El Burro by Angarita: hamburguesas gourmet'
]);

const pick = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};
const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** Una lista desde texto ("a, b" o una por linea) o desde un arreglo: limpia, sin vacios ni repetidos. */
export function lista(v) {
  const crudo = Array.isArray(v) ? v : texto(v).split(/[,\n]/);
  const vistos = new Set();
  const out = [];
  for (const x of crudo) {
    const t = texto(x);
    const clave = t.toLowerCase();
    if (t && !vistos.has(clave)) { vistos.add(clave); out.push(t); }
  }
  return out;
}

/** #RRGGBB en mayusculas, o null si no es un color de 6 cifras (acepta sin #). */
export function colorValido(v) {
  const t = texto(v).replace(/^#/, '');
  return /^[0-9a-fA-F]{6}$/.test(t) ? '#' + t.toUpperCase() : null;
}

/** Lo que falta para que la marca sirva a los prompts, con los nombres de campo del 422. */
export function loQueFalta(m) {
  const falta = [];
  if (!texto(m?.descripcion)) falta.push('descripcion');
  const p = lista(m?.productosTipicos).length;
  if (p < LIMITES.productosMin || p > LIMITES.productosMax) falta.push('productosTipicos');
  const v = lista(m?.valores).length;
  if (v < LIMITES.valoresMin || v > LIMITES.valoresMax) falta.push('valores');
  if (!colorValido(m?.colorHex)) falta.push('colorHex');
  return falta;
}

/** La respuesta del GET, normalizada. Si la API no dice completa/falta, se calcula con la misma regla. Nunca lanza. */
export function leerMarca(data) {
  const d = data && typeof data === 'object' ? data : {};
  const m = {
    descripcion: texto(pick(d, 'descripcion', 'Descripcion')),
    productosTipicos: lista(pick(d, 'productosTipicos', 'productos_tipicos', 'ProductosTipicos') ?? []),
    valores: lista(pick(d, 'valores', 'Valores') ?? []),
    colorHex: colorValido(pick(d, 'colorHex', 'color_hex', 'ColorHex')),
    colorNombre: texto(pick(d, 'colorNombre', 'color_nombre', 'ColorNombre'))
  };
  const faltaApi = pick(d, 'falta', 'Falta');
  const falta = Array.isArray(faltaApi) ? faltaApi.map(String) : loQueFalta(m);
  const completaApi = pick(d, 'completa', 'Completa');
  return { ...m, falta, completa: typeof completaApi === 'boolean' ? completaApi : falta.length === 0 };
}

/** El cuerpo del PUT: solo camelCase, listas limpias, color normalizado, vacios como null. */
export function cuerpoDeMarca(m) {
  return {
    descripcion: texto(m?.descripcion) || null,
    productosTipicos: lista(m?.productosTipicos),
    valores: lista(m?.valores),
    colorHex: colorValido(m?.colorHex),
    colorNombre: texto(m?.colorNombre) || null
  };
}

/** Nada escrito: el wizard no gasta un PUT en una marca vacia. */
export function marcaVacia(m) {
  const c = cuerpoDeMarca(m);
  return !c.descripcion && !c.productosTipicos.length && !c.valores.length && !c.colorHex && !c.colorNombre;
}

/** Por que no se pudo leer o guardar, decidido por el codigo HTTP y no por el texto (ApiBase lo traduce). */
export function motivoDeLaMarcaDelNegocio(res) {
  const codigo = Number(res?.code ?? res?.details?.status);
  if (codigo === 404) return { motivo: 'NO_PUBLICADO', frase: 'La API todavia no guarda la marca (falta publicar FND-T2). Lo escrito no se pierde de esta pantalla.' };
  if (codigo === 401) return { motivo: 'SIN_SESION', frase: 'La sesion vencio: vuelve a entrar.' };
  if (codigo === 403) return { motivo: 'PROHIBIDO', frase: 'No tienes acceso a este negocio.' };
  if (codigo === 400 || codigo === 422) return { motivo: 'RECHAZADA', frase: 'La API rechazo la marca: revisa los campos marcados.' };
  return { motivo: 'ERROR_API', frase: 'No pudimos hablar con la API ahora. Intenta de nuevo.' };
}
